import React from 'react';
import { cn } from '../lib/utils';
import Reveal from './Reveal';
import EditableText from './EditableText';

export default function PageHero({
  badge,
  eyebrow,
  eyebrowKey,
  title,
  titleKey,
  subtitle,
  subtitleKey,
  children,
  className = '',
  reveal = true,
}) {
  const label = eyebrow ?? badge;

  const labelNode =
    !label ? null : eyebrowKey && typeof label === 'string' ? (
      <EditableText
        editKey={eyebrowKey}
        defaultValue={label}
        as="p"
        className="text-[#3a00e0] text-sm font-semibold uppercase tracking-wider mb-3"
      />
    ) : typeof label === 'string' ? (
      <p className="text-[#3a00e0] text-sm font-semibold uppercase tracking-wider mb-3">
        {label}
      </p>
    ) : (
      <div className="flex justify-center mb-4">{label}</div>
    );

  const titleNode =
    titleKey && typeof title === 'string' ? (
      <EditableText
        editKey={titleKey}
        defaultValue={title}
        as="h1"
        className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight"
      />
    ) : (
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
        {title}
      </h1>
    );

  const subtitleClass = cn(
    'text-base sm:text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto',
    children && 'mb-8'
  );

  const subtitleNode =
    !subtitle ? null : subtitleKey && typeof subtitle === 'string' ? (
      <EditableText
        editKey={subtitleKey}
        defaultValue={subtitle}
        as="p"
        multiline
        className={subtitleClass}
      />
    ) : (
      <p className={subtitleClass}>{subtitle}</p>
    );

  const inner = (
    <div className="text-center max-w-3xl mx-auto">
      {labelNode}
      {titleNode}
      <span className="section-title-rule section-title-rule--center mb-5 sm:mb-6" aria-hidden="true" />
      {subtitleNode}
      {children}
    </div>
  );

  return (
    <section
      className={cn(
        'relative pt-below-site-header pb-12 sm:pb-16 px-4 overflow-hidden',
        className
      )}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[rgba(58,0,224,0.06)] via-white to-slate-50" />
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(58,0,224,0.12) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
        aria-hidden="true"
      />
      <div className="max-w-7xl mx-auto relative z-10">
        {reveal ? <Reveal animation="fade-up">{inner}</Reveal> : inner}
      </div>
    </section>
  );
}
